import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from "framer-motion";
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from './components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from './components/ui/dropdown-menu';
import { Switch } from './components/ui/switch';
import { toast, Toaster } from 'sonner';
import { 
  Settings, Edit, Trash2, Key, ChevronDown, MapPin, Plus, Upload, X, Palette, 
  CreditCard, AlertTriangle, Users, FileText, Clock, Mail, Eye, EyeOff, Loader2, 
  Save, LayoutDashboard, BarChart3, LogOut, Download, Search, Copy, Coffee, Calendar, CalendarDays
} from 'lucide-react';
import TymeLoader from './components/TymeLoader';
import analytics from './utils/analytics';
import TimeRulesSettings from './components/TimeManagement/TimeRulesSettings';
import RotaBuilder from './components/RotaBuilder';
// Extracted dashboard components
import { inputStyle, SidebarItem, MetricCard, DashboardPanel, ActionPill, AvatarCircle, getEmployeeInitials, EmployeeHoursRow, GlassButton, IOSGlassButton, GlassCard, GridTexture, ClockLogo } from './components/dashboard/DashboardUI';
import AuthScreens from './components/dashboard/AuthScreens';
import LiveStatusTab from './components/dashboard/LiveStatusTab';
import EmployeesTab from './components/dashboard/EmployeesTab';
import HoursTab from './components/dashboard/HoursTab';
import DashboardModals from './components/dashboard/DashboardModals';

// Compact toggle for rules section
const CompactRuleToggle = ({ label, description, checked, onCheckedChange }) => (
  <div className="flex items-center justify-between px-6 py-5">
    <div className="min-w-0">
      <div className="text-sm font-medium text-white">{label}</div>
      <div className="mt-1 text-xs text-zinc-400">{description}</div>
    </div>
    <Switch checked={checked} onCheckedChange={onCheckedChange} />
  </div>
);

import LocationKiosk from './components/LocationKiosk';
import AttendancePage from './AttendancePage';
import HomePage from './HomePage';
import SuperAdminDashboard from './SuperAdminDashboard';
import TimeTrackingSoftware from './TimeTrackingSoftware';
import SmallBusinessTimeClock from './SmallBusinessTimeClock';
import RetailAttendanceSoftware from './RetailAttendanceSoftware';
import './App.css';

// Backend URL configuration
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Test backend connection
const testConnection = async () => {
  try {
    await axios.get(`${API}/employees/initials`, { timeout: 5000 });
    return true;
  } catch (error) {
    console.warn('Backend connection failed:', error.message);
    return false;
  }
};
const KioskPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState('action'); // action, initial, name, pin, confirmation
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedInitial, setSelectedInitial] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [pin, setPin] = useState('');
  const [availableInitials, setAvailableInitials] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Reset kiosk to initial state
  const resetKiosk = () => {
    setCurrentStep('action');
    setSelectedAction('');
    setSelectedInitial('');
    setSelectedEmployee(null);
    setPin('');
    setEmployees([]);
  };

  // Fetch available initials
  const fetchInitials = async () => {
    try {
      const response = await axios.get(`${API}/employees/initials`);
      setAvailableInitials(response.data.initials);
    } catch (error) {
      console.error('Error fetching initials:', error);
      // Silently fail - initials may not be available
    }
  };

  // Fetch employees by initial with better error handling
  const fetchEmployeesByInitial = async (initial) => {
    try {
      const response = await axios.get(`${API}/employees/by-initial/${initial}`);
      setEmployees(response.data.employees);
      
      // If no employees found for this initial, show message
      if (response.data.employees.length === 0) {
        toast.info(`No employees found with initial "${initial}"`);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      
      // Show empty list and inform user
      setEmployees([]);
      toast.error(`Cannot load employees for initial "${initial}". Please contact IT support.`);
    }
  };

  // Handle kiosk submission - SECURE: No UI changes until PIN is verified
  const handleSubmit = async () => {
    if (!selectedEmployee || !pin) return;

    // Validate PIN format before sending
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) {
      toast.error('PIN must be exactly 4 digits');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/kiosk/submit`, {
        username: selectedEmployee.username,
        pin: pin.trim(), // Ensure no whitespace
        action: selectedAction
      });

      // Only show success and reset if PIN was verified and event was created
      if (response.data.success) {
        toast.success(response.data.message);
        
        // Auto-reset after 2 seconds only on successful PIN verification
        setTimeout(() => {
          resetKiosk();
        }, 2000);
      } else {
        toast.error('Attendance recording failed');
      }

    } catch (error) {
      const message = error.response?.data?.detail || 'An error occurred';
      
      // Handle different error types
      if (error.response?.status === 401) {
        toast.error('Incorrect PIN - Please try again');
        // Clear PIN field on incorrect PIN
        setPin('');
      } else if (error.response?.status === 423) {
        toast.error('Account temporarily locked due to too many incorrect attempts');
        // Reset to employee selection on lockout
        setCurrentStep('name');
        setPin('');
      } else if (error.response?.status === 400) {
        toast.error(message); // State machine errors (already checked in/out)
      } else if (error.response?.status === 429) {
        toast.error('Please wait before trying again');
      } else {
        toast.error(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle action selection
  const handleActionSelect = (action) => {
    setSelectedAction(action);
    setCurrentStep('initial');
    fetchInitials();
  };

  // Handle initial selection
  const handleInitialSelect = (initial) => {
    setSelectedInitial(initial);
    setCurrentStep('name');
    fetchEmployeesByInitial(initial);
  };

  // Handle employee selection
  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    setCurrentStep('pin');
  };

  // Handle PIN input - SECURE: Only allow 4 digits, no special characters
  const handlePinChange = (value) => {
    // Strip any non-digit characters and limit to 4 characters
    const cleanValue = value.replace(/\D/g, '').slice(0, 4);
    setPin(cleanValue);
  };

  // Handle PIN submission
  const handlePinSubmit = () => {
    if (pin.length === 4) {
      handleSubmit();
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'action':
        return (
          <div className="space-y-8">
            <h2 className="text-2xl font-semibold text-center text-slate-700">
              Choose your action
            </h2>
            <div className="grid grid-cols-2 gap-6">
              <Button
                size="lg"
                className="h-24 text-xl font-medium bg-slate-400 hover:bg-slate-500 text-white shadow-lg"
                onClick={() => handleActionSelect('check_in')}
              >
                Check In
              </Button>
              <Button
                size="lg"
                className="h-24 text-xl font-medium bg-slate-400 hover:bg-slate-500 text-white shadow-lg"
                onClick={() => handleActionSelect('check_out')}
              >
                Check Out
              </Button>
            </div>
          </div>
        );

      case 'initial':
        return (
          <div className="space-y-8 relative">
            <button
              onClick={resetKiosk}
              className="absolute top-0 left-0 p-2 text-slate-600 hover:text-slate-800 transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="text-center">
              <Badge className="mb-4 bg-slate-100 text-slate-700">
                {selectedAction === 'check_in' ? 'Check In' : 'Check Out'}
              </Badge>
              <h2 className="text-2xl font-semibold text-slate-700">
                First name initial
              </h2>
            </div>
            <div className="flex justify-center">
              <div className="grid grid-cols-6 gap-3 max-w-md">
                {availableInitials.map((initial) => (
                  <Button
                    key={initial}
                    variant="outline"
                    className="h-16 text-lg font-medium border-2 hover:bg-slate-50"
                    onClick={() => handleInitialSelect(initial)}
                  >
                    {initial}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'name':
        return (
          <div className="space-y-8 relative">
            <button
              onClick={() => setCurrentStep('initial')}
              className="absolute top-0 left-0 p-2 text-slate-600 hover:text-slate-800 transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="text-center">
              <Badge className="mb-4 bg-slate-100 text-slate-700">
                {selectedAction === 'check_in' ? 'Check In' : 'Check Out'} → {selectedInitial}
              </Badge>
              <h2 className="text-2xl font-semibold text-slate-700">
                Select your name
              </h2>
            </div>
            <div className="space-y-3">
              {employees.map((employee) => (
                <Button
                  key={employee.id}
                  variant="outline"
                  className="w-full h-16 text-left border-2 hover:bg-slate-50"
                  onClick={() => handleEmployeeSelect(employee)}
                >
                  <div>
                    <div className="text-lg font-medium">
                      {employee.first_name} {employee.last_name}
                    </div>
                    <div className="text-sm text-slate-500">
                      {employee.username}
                    </div>
                  </div>
                </Button>
              ))}
            </div>
          </div>
        );

      case 'pin':
        return (
          <div className="space-y-8 relative">
            <button
              onClick={() => setCurrentStep('name')}
              className="absolute top-0 left-0 p-2 text-slate-600 hover:text-slate-800 transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="text-center">
              <Badge className="mb-4 bg-slate-100 text-slate-700">
                {selectedEmployee?.first_name} {selectedEmployee?.last_name}
              </Badge>
              <h2 className="text-2xl font-semibold text-slate-700">
                Enter your 4-digit PIN
              </h2>
            </div>
            <div className="flex justify-center">
              <Input
                type="password"
                placeholder="••••"
                value={pin}
                onChange={(e) => handlePinChange(e.target.value)}
                className="w-32 h-16 text-center text-2xl font-mono border-2"
                maxLength={4}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handlePinSubmit();
                  }
                }}
              />
            </div>
            <div className="text-center">
              <Button
                size="lg"
                className="w-40 h-12 bg-slate-700 hover:bg-slate-800 text-white"
                onClick={handlePinSubmit}
                disabled={pin.length !== 4 || isLoading}
              >
                {isLoading ? 'Processing...' : 'Submit'}
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6 relative">
      <Card className="w-full max-w-2xl shadow-xl border-0 bg-white rounded-lg">
        <CardContent className="p-12">
          {renderCurrentStep()}
        </CardContent>
      </Card>

      {/* Discreet Manager Button */}
      <button
        onClick={() => navigate('/manager-dashboard')}
        className="fixed bottom-6 left-6 p-3 bg-slate-200 hover:bg-slate-300 text-slate-600 hover:text-slate-700 rounded-full shadow-md transition-all duration-200 opacity-30 hover:opacity-100"
        title="Dashboard"
      >
        <Settings size={16} />
      </button>
    </div>
  );
};

const Dashboard = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [userName, setUserName] = useState('');  // Store user's full name for welcome message
  const [showSignup, setShowSignup] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [showPaymentRequired, setShowPaymentRequired] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');
  const [activeTab, setActiveTab] = useState('live-status');
  const [liveStatus, setLiveStatus] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [reportEmployees, setReportEmployees] = useState([]);
  const [reportInfo, setReportInfo] = useState(null);
  const [automatedReports, setAutomatedReports] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [emailSettings, setEmailSettings] = useState(null); // Load from API, don't use defaults
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [showDeleteLocationModal, setShowDeleteLocationModal] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);
  
  // Plan information state
  const [planInfo, setPlanInfo] = useState(null);
  const [isPlanLoading, setIsPlanLoading] = useState(false);
  
  // Plan feature helpers
  const hasRotaBuilder = planInfo?.limits?.has_rota_builder || planInfo?.is_vip || false;
  const planName = planInfo?.plan ? planInfo.plan.charAt(0).toUpperCase() + planInfo.plan.slice(1) : '';
  const maxEmployees = planInfo?.limits?.max_employees || 15;
  
  // Subscription management state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [showSubscriptionManagement, setShowSubscriptionManagement] = useState(false);
  const [subscriptionAction, setSubscriptionAction] = useState(null); // 'cancel', 'upgrade'
  const [upgradeTarget, setUpgradeTarget] = useState('pro'); // Target plan for upgrades

  // Staff Hours Overview state
  const [staffHours, setStaffHours] = useState([]);
  const [staffHoursLoading, setStaffHoursLoading] = useState(false);
  const [staffHoursSearch, setStaffHoursSearch] = useState('');
  const [staffHoursPeriod, setStaffHoursPeriod] = useState('this_week');
  const [staffHoursAvailableMonths, setStaffHoursAvailableMonths] = useState([]);
  
  // Leave Analytics state
  const [leaveAnalytics, setLeaveAnalytics] = useState(null);
  const [leaveAnalyticsLoading, setLeaveAnalyticsLoading] = useState(false);
  const [leaveAnalyticsPeriod, setLeaveAnalyticsPeriod] = useState('ytd');
  
  // Email automation state (Business plan)
  const [emailAutomation, setEmailAutomation] = useState({
    exists: false,
    email_address: '',
    delivery_day: 'monday',
    enabled: true
  });
  const [showEmailAutomationDialog, setShowEmailAutomationDialog] = useState(false);
  const [emailAutomationLoading, setEmailAutomationLoading] = useState(false);
  
  // Weekly Hours Email state (Available to All Plans)
  const [weeklyHoursEmail, setWeeklyHoursEmail] = useState({
    exists: false,
    email_address: '',
    delivery_day: 'friday',
    frequency: 'weekly',
    enabled: true
  });
  const [weeklyHoursEmailLoading, setWeeklyHoursEmailLoading] = useState(false);
  
  // Edit modal states
  const [editPinDialog, setEditPinDialog] = useState(false);
  const [editNameDialog, setEditNameDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [breakSettingsDialog, setBreakSettingsDialog] = useState(false);
  const [employeeBreakForm, setEmployeeBreakForm] = useState({ custom_break_minutes: 30, custom_work_hours: 8.0 });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editPinForm, setEditPinForm] = useState({ pin: '' });
  const [editNameForm, setEditNameForm] = useState({ first_name: '', last_name: '', pin: '', email: '' });
  const [showPinInEdit, setShowPinInEdit] = useState(false);
  
  // Time Management states
  const [showScheduleEditor, setShowScheduleEditor] = useState(false);
  const [employeeToEdit, setEmployeeToEdit] = useState(null);
  const [showTimeRulesSettings, setShowTimeRulesSettings] = useState(false);
  const [showAllScheduleEditor, setShowAllScheduleEditor] = useState(false);
  
  // Auto-refresh state for live updates
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  
  // Employee status management state
  const [employeeStatusUpdate, setEmployeeStatusUpdate] = useState({
    employeeId: null,
    status: 'active',
    note: '',
    startDate: '',
    endDate: ''
  });
  const [showStatusModal, setShowStatusModal] = useState(false);
  
  // Location state
  const [locations, setLocations] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [addLocationDialog, setAddLocationDialog] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [showLocationKiosk, setShowLocationKiosk] = useState(false);
  const [showRotaLocationSelect, setShowRotaLocationSelect] = useState(false);
  
  // Customization state
  const [customization, setCustomization] = useState({
    button_shape: 'rectangle', // Default shape
    background_theme: 'white', // Default background theme
    logo_url: null,
    logo_filename: null,
    attendance_page_title: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  
  const [isLoading, setIsLoading] = useState(false);

  // Login form state
  const [loginForm, setLoginForm] = useState({ email_or_name: '', password: '' });

  // Signup form state
  const [signupForm, setSignupForm] = useState({
    name: '',
    email: '',
    password: '',
    confirm_password: '',
    acceptTerms: false,
    acceptPrivacy: false
  });

  // Modal states for Terms and Privacy Policy
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // Email verification state
  const [verificationForm, setVerificationForm] = useState({
    email: '',
    verification_code: ''
  });

  // Password validation state
  const [passwordErrors, setPasswordErrors] = useState([]);
  
  // Form validation states
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState('email'); // 'email', 'code', 'reset'
  const [forgotPasswordForm, setForgotPasswordForm] = useState({
    email: '',
    verification_code: '',
    new_password: '',
    confirm_password: ''
  });
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');

  // New employee form state
  const [newEmployeeForm, setNewEmployeeForm] = useState({
    first_name: '',
    last_name: '',
    pin: '',
    email: ''
  });

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validateFullName = (name) => {
    if (!name) {
      return 'Full name is required';
    }
    const nameParts = name.trim().split(/\s+/);
    if (nameParts.length < 2) {
      return 'Please enter your full name (first and last name)';
    }
    if (nameParts.some(part => part.length < 2)) {
      return 'Each name must be at least 2 characters long';
    }
    return '';
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push('At least 8 characters long');
    }
    if (!/(?=.*[a-z])/.test(password)) {
      errors.push('At least one lowercase letter');
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      errors.push('At least one uppercase letter');
    }
    if (!/(?=.*\d)/.test(password)) {
      errors.push('At least one number');
    }
    if (!/(?=.*[@$!%*?&])/.test(password)) {
      errors.push('At least one special character (@$!%*?&)');
    }
    return errors;
  };

  // Check for existing session on component mount
  useEffect(() => {
    const savedSession = localStorage.getItem('tyme_session');
    const savedUser = localStorage.getItem('tyme_user');
    
    // Check if returning from Stripe payment
    const urlParams = new URLSearchParams(window.location.search);
    const isReturningFromPayment = urlParams.get('payment') === 'success' || window.location.href.includes('stripe');
    
    if (savedSession && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        console.log('Restoring saved session for user:', userData.type);
        setSessionId(savedSession);
        setUserName(userData.name || '');  // Restore user name
        
        // If returning from payment, wait a bit for webhook to process and retry
        const recheckPlan = async (retryCount = 0, maxRetries = 5) => {
          console.log(`Checking plan status (attempt ${retryCount + 1}/${maxRetries})...`);
          
          const { isValid, hasPlan } = await verifySessionAndPlan(savedSession);
          
          if (isValid) {
            console.log('Restored session is valid');
            window.loginTime = Date.now();
            
            if (hasPlan) {
              // User has a plan - grant access to dashboard
              console.log('✅ User has a plan - logging in');
              setIsLoggedIn(true);
              // Clean up URL parameters
              if (isReturningFromPayment) {
                window.history.replaceState({}, document.title, window.location.pathname);
              }
            } else if (isReturningFromPayment && retryCount < maxRetries) {
              // User just paid but webhook might not have processed yet - retry
              console.log(`⏳ Payment detected but plan not yet updated. Retrying in 2 seconds...`);
              setTimeout(() => recheckPlan(retryCount + 1, maxRetries), 2000);
            } else {
              // User has NO plan - redirect to payment page
              console.log('❌ User has no plan - showing payment page');
              setShowPaymentRequired(true);
            }
          } else {
            console.log('Session expired or backend restarted - user needs to login again');
          }
        };
        
        recheckPlan().catch((error) => {
          console.error('Session verification error:', error);
          // Don't clear session immediately if returning from payment
          if (!isReturningFromPayment) {
            localStorage.removeItem('tyme_session');
            localStorage.removeItem('tyme_user');
          }
        });
      } catch (error) {
        console.error('Error parsing saved session data:', error);
        // Clear corrupted session data
        localStorage.removeItem('tyme_session');
        localStorage.removeItem('tyme_user');
      }
    }
  }, []);

  // Centralized error handler for API calls
  const handleApiError = (error, operation = 'operation') => {
    console.error(`Error during ${operation}:`, error);
    
    // Track error for analytics
    const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
    analytics.trackError(operation, errorMessage);
    
    // Don't show toast errors for background/loading operations
    const silentOperations = [
      'loading locations', 
      'loading employees', 
      'fetching plan info',
      'loading staff hours',
      'loading overtime info',
      'loading time rules',
      'loading rota data',
      'initial load'
    ];
    
    const isSilentOperation = silentOperations.some(op => operation.toLowerCase().includes(op.toLowerCase()));
    
    // Check for authentication errors - but NEVER auto-logout users
    if (error.response?.status === 401) {
      console.log('401 error detected for operation:', operation, '- but keeping user logged in');
      // Don't show error toast, don't logout - just log it
      return false;
    }
    
    // For non-silent operations, you could optionally show errors
    // But for now, keep the app quiet and just log
    if (!isSilentOperation) {
      console.log('Non-silent operation error:', operation, errorMessage);
    }
    
    return false; // No logout performed
  };

  // Verify session is still valid - but be very forgiving
  const verifySession = async (sessionId) => {
    try {
      console.log('Verifying session:', sessionId);
      await axios.get(`${API}/manager/locations`, {
        params: { session_id: sessionId }
      });
      console.log('Session verification successful');
      return true;
    } catch (error) {
      console.log('Session verification had issues, but keeping user logged in:', error.response?.status);
      // Don't clear session data - keep user logged in even if verification fails
      // Backend will auto-restore sessions after restart
      return true; // Always return true to keep users logged in
    }
  };

  // Verify session AND check if user has a paid plan
  const verifySessionAndPlan = async (sessionId) => {
    try {
      console.log('Verifying session and plan status:', sessionId);
      
      // Check plan-info to see if user has a plan
      const response = await axios.get(`${API}/user/plan-info`, {
        params: { session_id: sessionId }
      });
      
      const planInfo = response.data;
      const userPlan = planInfo.plan;
      
      // User has a plan if it's starter, growth, pro, or enterprise
      const hasPlan = userPlan && ['starter', 'growth', 'pro', 'enterprise'].includes(userPlan);
      
      console.log('Session verification result:', {
        isValid: true,
        plan: userPlan,
        hasPlan: hasPlan
      });
      
      return {
        isValid: true,
        hasPlan: hasPlan
      };
    } catch (error) {
      console.log('Session/plan verification error:', error.response?.status);
      
      // If 401/403, session is invalid - user needs to login again
      if (error.response?.status === 401 || error.response?.status === 403) {
        return {
          isValid: false,
          hasPlan: false
        };
      }
      
      // For other errors (network issues, server errors), give user benefit of doubt
      // Let them into dashboard - if session is truly invalid, API calls will fail gracefully
      console.log('Temporary error - granting access, will verify on API calls');
      return {
        isValid: true,
        hasPlan: true // Grant access on temporary errors to avoid locking out paying users
      };
    }
  };

  // Helper function to create Stripe checkout session with proper redirect
  const getStripePaymentUrl = async (plan) => {
    try {
      // Get user email from localStorage
      let userEmail = null;
      try {
        const savedUser = localStorage.getItem('tyme_user');
        if (savedUser) {
          const userData = JSON.parse(savedUser);
          userEmail = userData.email || userData.username;
        }
      } catch (error) {
        console.error('Error getting user email:', error);
      }
      
      // Create checkout session via backend
      const response = await axios.post(`${API}/stripe/create-checkout-session`, {
        plan: plan,
        email: userEmail
      });
      
      if (response.data.success) {
        return response.data.checkout_url;
      }
      
      return null;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('Failed to create payment session. Please try again.');
      return null;
    }
  };

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // First try manager login if the input looks like a username (no @ and matches "Sibel")
      if (!loginForm.email_or_name.includes('@') && loginForm.email_or_name.toLowerCase() === 'sibel') {
        try {
          const managerResponse = await axios.post(`${API}/manager/login`, {
            username: loginForm.email_or_name,
            password: loginForm.password
          });
          
          if (managerResponse.data.success) {
            // Track login time for session protection
            window.loginTime = Date.now();
            
            setSessionId(managerResponse.data.session_id);
            setIsLoggedIn(true);
            
            // Save manager session
            localStorage.setItem('tyme_session', managerResponse.data.session_id);
            localStorage.setItem('tyme_user', JSON.stringify({ 
              type: 'manager', 
              username: loginForm.email_or_name 
            }));
            
            toast.success('Manager login successful');
            // Don't call loadLiveStatus immediately - let useEffect handle data loading
            return;
          }
        } catch (managerError) {
          // If manager login fails, continue to user login
        }
      }
      
      // Try user login (supports both email and full name)
      const response = await axios.post(`${API}/auth/login`, {
        email_or_name: loginForm.email_or_name,
        password: loginForm.password
      });
      
      if (response.data.session_id) {
        setSessionId(response.data.session_id);
        
        if (response.data.subscription_required) {
          // User needs to complete subscription
          setShowPaymentRequired(true);
          toast.info(response.data.message);
        } else {
          // Track login time for session protection
          window.loginTime = Date.now();
          
          // User has active subscription OR is admin (tayla@fadil)
          setIsLoggedIn(true);
          setUserName(response.data.user.name || '');  // Store user name
          
          // Save user session
          localStorage.setItem('tyme_session', response.data.session_id);
          localStorage.setItem('tyme_user', JSON.stringify({ 
            type: response.data.user.subscription_status === 'admin' ? 'admin' : 'user',
            ...response.data.user 
          }));
          
          // Track successful login
          analytics.trackLogin('email');
          
          toast.success(response.data.message);
          // Don't call loadLiveStatus immediately - let useEffect handle data loading
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Password validation functions
  const validatePasswordRealTime = (password, email) => {
    const errors = [];
    
    if (password.length > 0 && password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    if (password.length >= 4) {
      // Check for repetitive patterns
      const hasRepetitive = /1234|4321|abcd|dcba|0123|qwer|asdf|zxcv/.test(password.toLowerCase()) ||
                           /(.)\1{2,}/.test(password) || // 3+ same characters in a row
                           /(?:012|123|234|345|456|567|678|789|890)/.test(password) ||
                           /(?:abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz)/.test(password.toLowerCase());
      
      if (hasRepetitive) {
        errors.push('Password cannot contain repetitive patterns (1234, abcd, aaaa)');
      }
    }
    
    if (email && password.length >= 3) {
      const emailParts = email.toLowerCase().split('@')[0];
      if (emailParts.length >= 3 && password.toLowerCase().includes(emailParts)) {
        errors.push('Password cannot contain parts of your email address');
      }
    }
    
    return errors;
  };

  // Handle signup
  const handleSignup = async (e) => {
    e.preventDefault();
    
    // Reset errors
    setEmailError('');
    setNameError('');
    setPasswordErrors([]);
    
    // Check terms and privacy acceptance
    if (!signupForm.acceptTerms || !signupForm.acceptPrivacy) {
      toast.error('You must accept both the Terms & Conditions and Privacy Policy to create an account');
      return;
    }
    
    // Validate email
    const emailValidation = validateEmail(signupForm.email);
    if (emailValidation) {
      setEmailError(emailValidation);
      return;
    }
    
    // Validate full name
    const nameValidation = validateFullName(signupForm.name);
    if (nameValidation) {
      setNameError(nameValidation);
      return;
    }
    
    // Validate password
    const passwordValidation = validatePassword(signupForm.password);
    if (passwordValidation.length > 0) {
      setPasswordErrors(passwordValidation);
      return;
    }
    
    if (signupForm.password !== signupForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Send signup request with verification code
      const response = await axios.post(`${API}/auth/signup`, {
        name: signupForm.name,
        email: signupForm.email,
        password: signupForm.password,
        confirm_password: signupForm.confirm_password
      });
      
      // Success - show verification screen
      setSignupEmail(signupForm.email);
      setVerificationForm({ email: signupForm.email, verification_code: '' });
      setShowSignup(false);
      setShowEmailVerification(true);
      toast.success(response.data.message || 'Verification code sent to your email!');
      analytics.trackSignup('email');
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Signup failed. Please try again.';
      toast.error(message);
      handleApiError(error, 'signup');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle forgot password - step 1: send code
  const handleForgotPasswordSendCode = async (e) => {
    e.preventDefault();
    
    const emailValidation = validateEmail(forgotPasswordForm.email);
    if (emailValidation) {
      setForgotPasswordMessage(emailValidation);
      return;
    }
    
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email: forgotPasswordForm.email.toLowerCase()
      });
      
      setForgotPasswordStep('code');
      setForgotPasswordMessage('If an account with that email exists, we\'ve sent you a 6-digit verification code.');
      
    } catch (error) {
      // Always show neutral message to prevent email enumeration
      setForgotPasswordStep('code');
      setForgotPasswordMessage('If an account with that email exists, we\'ve sent you a 6-digit verification code.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle forgot password - step 2: verify code and reset password
  const handleForgotPasswordReset = async (e) => {
    e.preventDefault();
    
    if (!forgotPasswordForm.verification_code || forgotPasswordForm.verification_code.length !== 6) {
      setForgotPasswordMessage('Please enter a valid 6-digit code.');
      return;
    }
    
    const passwordValidation = validatePassword(forgotPasswordForm.new_password);
    if (passwordValidation.length > 0) {
      setForgotPasswordMessage('Password must meet all requirements.');
      return;
    }
    
    if (forgotPasswordForm.new_password !== forgotPasswordForm.confirm_password) {
      setForgotPasswordMessage('Passwords do not match.');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: forgotPasswordForm.email.toLowerCase(),
        verification_code: forgotPasswordForm.verification_code,
        new_password: forgotPasswordForm.new_password
      });
      
      toast.success('Password reset successfully! Please log in with your new password.');
      setShowForgotPassword(false);
      setForgotPasswordStep('email');
      setForgotPasswordForm({
        email: '',
        verification_code: '',
        new_password: '',
        confirm_password: ''
      });
      setForgotPasswordMessage('');
      
    } catch (error) {
      setForgotPasswordMessage(error.response?.data?.detail || 'Invalid or expired code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle email verification
  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/verify-email`, verificationForm);
      if (response.data.session_id) {
        setSessionId(response.data.session_id);
        
        // Get user name from response or from signup form
        const userName = response.data.user?.name || signupForm.name || '';
        setUserName(userName);
        
        // Save session data to localStorage with name
        localStorage.setItem('tyme_session', response.data.session_id);
        localStorage.setItem('tyme_user', JSON.stringify({
          email: verificationForm.email,
          name: userName
        }));
        
        // Track account creation conversion
        const plan = response.data.plan || 'none';
        analytics.trackAccountCreated(plan);
        
        setShowEmailVerification(false);
        
        // CHECK IF USER HAS A PLAN - CRITICAL FIX
        if (response.data.subscription_required) {
          // User has NO plan - show payment page
          setShowPaymentRequired(true);
          toast.success('Account created! Please select a plan to continue.');
        } else {
          // User has a plan - can access dashboard
          setIsLoggedIn(true);
          toast.success('Account created successfully! Welcome to TYME');
          loadLiveStatus(response.data.session_id);
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Verification failed';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle resend verification code
  const handleResendCode = async () => {
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/auth/resend-code`, { email: verificationForm.email });
      toast.success('New verification code sent to your email');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to resend code';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Load live status and employees (unified dashboard - no location filtering)
  const loadLiveStatus = async (sid = sessionId) => {
    if (!sid) return;
    
    try {
      const response = await axios.get(`${API}/manager/dashboard?session_id=${sid}`);
      
      // Transform response to match expected format
      const checkedIn = response.data.checked_in || [];
      const checkedOut = response.data.checked_out || [];
      const onBreak = response.data.on_break || [];
      const recentActivity = response.data.recent_activity || [];
      
      setLiveStatus({
        checked_in: checkedIn.map(emp => ({
          id: emp.id,
          name: emp.name,
          time: emp.since,
          is_late: emp.is_late || false
        })),
        checked_out: checkedOut.map(emp => ({
          id: emp.id,
          name: emp.name,
          time: emp.since
        })),
        on_break: onBreak.map(emp => ({
          id: emp.id,
          name: emp.name,
          time: emp.since
        })),
        kpis: {
          employees_checked_in: checkedIn.length,
          employees_on_break: onBreak.length,
          total_checkins_today: recentActivity.filter(a => a.event_type === 'check_in').length,
          open_sessions: checkedIn.length + onBreak.length
        },
        recent_activity: recentActivity
      });
      
      // Also set employees from the unified response
      if (response.data.employees) {
        setEmployees(response.data.employees);
      }
    } catch (error) {
      handleApiError(error, 'loading dashboard data');
    }
  };

  // Load employees (now uses unified dashboard data)
  const loadEmployees = async () => {
    if (!sessionId) return;
    
    try {
      const response = await axios.get(`${API}/manager/dashboard?session_id=${sessionId}`);
      setEmployees(response.data.employees || []);
    } catch (error) {
      handleApiError(error, 'loading employees');
    }
  };

  // Load leave analytics
  const loadLeaveAnalytics = async () => {
    if (!currentLocation) return;
    
    setLeaveAnalyticsLoading(true);
    try {
      const response = await axios.get(
        `${API}/manager/leave-analytics/${currentLocation.id}?session_id=${sessionId}&period=${leaveAnalyticsPeriod}`
      );
      setLeaveAnalytics(response.data);
    } catch (error) {
      handleApiError(error, 'loading leave analytics');
      setLeaveAnalytics(null);
    } finally {
      setLeaveAnalyticsLoading(false);
    }
  };

  // Load email settings for current location
  const loadEmailSettings = async () => {
    if (!currentLocation) return;
    
    try {
      const response = await axios.get(`${API}/manager/locations?session_id=${sessionId}`);
      const location = response.data.locations?.find(loc => loc.id === currentLocation.id);
      if (location?.email_report_settings) {
        setEmailSettings(location.email_report_settings);
      }
    } catch (error) {
      console.error('Error loading email settings:', error);
    }
  };

  // Save email settings
  const handleSaveEmailSettings = async () => {
    if (!currentLocation || !emailSettings) return;
    
    setIsLoading(true);
    try {
      const response = await axios.put(
        `${API}/manager/location/${currentLocation.id}/email-report-settings`,
        emailSettings,
        { params: { session_id: sessionId } }
      );

      if (response.data.success) {
        toast.success('Email report settings updated successfully');
      }
    } catch (error) {
      console.error('Error updating email settings:', error);
      toast.error('Failed to update email settings');
    } finally {
      setIsLoading(false);
    }
  };

  // Filter employees based on search term and status filter
  const getFilteredEmployees = () => {
    if (!employees) return [];
    
    let filtered = employees.filter(employee => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const fullName = `${employee.first_name} ${employee.last_name}`.toLowerCase();
        if (!fullName.includes(searchLower)) {
          return false;
        }
      }
      
      // Status filter
      if (statusFilter && statusFilter !== 'all') {
        if (statusFilter === 'on_leave') {
          // Show all employees who are not active
          return employee.current_status !== 'active';
        } else if (statusFilter === 'inactive') {
          // Show inactive employees
          return !employee.active;
        } else {
          // Show employees with specific status
          const employeeStatus = employee.current_status || 'active';
          return employeeStatus === statusFilter;
        }
      }
      
      return true;
    });
    
    return filtered;
  };

  // Load report status
  const loadReportStatus = async () => {
    if (!currentLocation) return;
    
    try {
      const response = await axios.get(`${API}/manager/weekly-reports/status/${currentLocation.id}?session_id=${sessionId}`);
      setReportInfo(response.data);
    } catch (error) {
      handleApiError(error, 'loading report status');
    }
  };

  // Load automated reports
  const loadAutomatedReports = async () => {
    if (!currentLocation) return;
    
    try {
      const response = await axios.get(`${API}/manager/automated-reports/list/${currentLocation.id}?session_id=${sessionId}`);
      setAutomatedReports(response.data.reports);
    } catch (error) {
      handleApiError(error, 'loading automated reports');
    }
  };

  // Download automated report
  const downloadAutomatedReport = async (filename) => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${API}/manager/automated-reports/download/${filename}?session_id=${sessionId}`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Automated report downloaded successfully');
    } catch (error) {
      let message = 'Error downloading automated report';
      if (error.response?.data instanceof Blob) {
        try { const text = await error.response.data.text(); const json = JSON.parse(text); message = json.detail || message; } catch(e) {}
      } else {
        message = error.response?.data?.detail || message;
      }
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Load available months for download
  const loadAvailableMonths = async () => {
    try {
      const response = await axios.get(`${API}/manager/reports/available-months?session_id=${sessionId}`);
      setAvailableMonths(response.data.available_months);
    } catch (error) {
      console.error('Error loading available months:', error);
    }
  };

  // Download days report (7 or 14 days)
  const downloadDaysReport = async (days) => {
    if (!currentLocation) {
      toast.error('Please select a location first');
      return;
    }
    
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${API}/manager/reports/download-days/${currentLocation.id}/${days}?session_id=${sessionId}`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `attendance_last_${days}_days_${currentLocation.name.replace(/\s+/g, '_')}.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Last ${days} days report downloaded for ${currentLocation.name}`);
    } catch (error) {
      let message = `Error downloading ${days} days report`;
      if (error.response?.data instanceof Blob) {
        try { const text = await error.response.data.text(); const json = JSON.parse(text); message = json.detail || message; } catch(e) {}
      } else {
        message = error.response?.data?.detail || message;
      }
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Download month report
  const downloadMonthReport = async () => {
    if (!selectedMonth) {
      toast.error('Please select a month');
      return;
    }
    
    if (!currentLocation) {
      toast.error('Please select a location first');
      return;
    }

    try {
      setIsLoading(true);
      const [year, month] = selectedMonth.split('-');
      const response = await axios.get(
        `${API}/manager/reports/download-month/${currentLocation.id}/${year}/${month}?session_id=${sessionId}`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `attendance_monthly_report_${currentLocation.name.replace(/\s+/g, '_')}.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // Find the selected month display name
      const monthData = availableMonths.find(m => m.value === selectedMonth);
      const monthName = monthData ? monthData.display_name : selectedMonth;
      
      toast.success(`${monthName} report downloaded for ${currentLocation.name}`);
    } catch (error) {
      let message = 'Error downloading monthly report';
      if (error.response?.data instanceof Blob) {
        try { const text = await error.response.data.text(); const json = JSON.parse(text); message = json.detail || message; } catch(e) {}
      } else {
        message = error.response?.data?.detail || message;
      }
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Download all employees report
  const downloadAllEmployeesReport = async () => {
    if (!currentLocation) {
      toast.error('Please select a location first');
      return;
    }
    
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${API}/manager/weekly-reports/download-all/${currentLocation.id}?session_id=${sessionId}`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `attendance_all_employees_${currentLocation.name.replace(/\s+/g, '_')}.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`All employees report downloaded for ${currentLocation.name}`);
    } catch (error) {
      let message = 'Error downloading report';
      if (error.response?.data instanceof Blob) {
        try { const text = await error.response.data.text(); const json = JSON.parse(text); message = json.detail || message; } catch(e) {}
      } else {
        message = error.response?.data?.detail || message;
      }
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Download all employees report - this month (1st to today)
  const downloadThisMonthReport = async () => {
    if (!currentLocation) {
      toast.error('Please select a location first');
      return;
    }
    
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${API}/manager/reports/download-this-month/${currentLocation.id}?session_id=${sessionId}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `this_month_${currentLocation.name.replace(/\s+/g, '_')}.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`This month's report downloaded for ${currentLocation.name}`);
    } catch (error) {
      let message = 'Error downloading report';
      if (error.response?.data instanceof Blob) {
        try { const text = await error.response.data.text(); const json = JSON.parse(text); message = json.detail || message; } catch(e) {}
      } else {
        message = error.response?.data?.detail || message;
      }
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Create new employee
  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    if (!currentLocation) {
      toast.error('Please select a location first');
      return;
    }
    
    setIsLoading(true);

    try {
      await axios.post(`${API}/manager/employees/${currentLocation.id}?session_id=${sessionId}`, {
        ...newEmployeeForm,
        location_id: currentLocation.id
      });
      toast.success('Employee created successfully');
      setNewEmployeeForm({ first_name: '', last_name: '', pin: '', email: '' });
      loadEmployees();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error creating employee';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle employee active status
  const toggleEmployeeStatus = async (employeeId, currentStatus) => {
    try {
      await axios.put(`${API}/manager/employees/${employeeId}?session_id=${sessionId}`, {
        active: !currentStatus
      });
      toast.success('Employee status updated');
      loadEmployees();
    } catch (error) {
      toast.error('Error updating employee status');
    }
  };

  // Update employee status
  const updateEmployeeStatus = async () => {
    if (!employeeStatusUpdate.employeeId) return;

    try {
      const statusData = {
        status: employeeStatusUpdate.status,
        note: employeeStatusUpdate.note || null,
        start_date: employeeStatusUpdate.startDate || null,
        end_date: employeeStatusUpdate.endDate || null
      };

      await axios.put(
        `${API}/manager/employees/${employeeStatusUpdate.employeeId}/status?session_id=${sessionId}`,
        statusData
      );

      // Refresh employee list
      await loadEmployees();
      
      // Close modal and reset form
      setShowStatusModal(false);
      setEmployeeStatusUpdate({
        employeeId: null,
        status: 'active',
        note: '',
        startDate: '',
        endDate: ''
      });

      toast.success('Employee status updated successfully');
    } catch (error) {
      handleApiError(error, 'updating employee status');
      const message = error.response?.data?.detail || 'Error updating employee status';
      if (error.response?.status !== 401) {
        toast.error(message);
      }
    }
  };

  // Open status update modal
  const openStatusModal = (employee) => {
    setEmployeeStatusUpdate({
      employeeId: employee.id,
      status: employee.current_status || 'active',
      note: employee.status_note || '',
      startDate: '',
      endDate: ''
    });
    setShowStatusModal(true);
  };

  // Cancel status update
  const cancelStatusUpdate = () => {
    setShowStatusModal(false);
    setEmployeeStatusUpdate({
      employeeId: null,
      status: 'active',
      note: '',
      startDate: '',
      endDate: ''
    });
  };

  // Close employee session
  const closeEmployeeSession = async (employeeId) => {
    if (!currentLocation) return;
    
    try {
      await axios.post(`${API}/manager/employees/${currentLocation.id}/${employeeId}/close-session?session_id=${sessionId}`);
      toast.success('Session closed successfully');
      loadLiveStatus();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error closing session';
      toast.error(message);
    }
  };

  // Handle delete location
  const handleDeleteLocation = (location) => {
    setLocationToDelete(location);
    setShowDeleteLocationModal(true);
  };

  const confirmDeleteLocation = async () => {
    if (!locationToDelete) return;
    
    setIsLoading(true);
    try {
      const response = await axios.delete(`${API}/manager/locations/${locationToDelete.id}`, {
        params: { session_id: sessionId }
      });
      
      if (response.data.success) {
        toast.success('Location and all its data deleted successfully');
        // Refresh locations list
        await loadLocations(sessionId);
        // If deleted location was selected, switch to first available location
        if (currentLocation?.id === locationToDelete.id) {
          const remainingLocations = locations.filter(loc => loc.id !== locationToDelete.id);
          if (remainingLocations.length > 0) {
            setCurrentLocation(remainingLocations[0]);
            loadLiveStatus(sessionId, remainingLocations[0].id);
          } else {
            setCurrentLocation(null);
          }
        }
      }
    } catch (error) {
      handleApiError(error, 'deleting location');
      const message = error.response?.data?.detail || 'Failed to delete location';
      toast.error(message);
    } finally {
      setIsLoading(false);
      setShowDeleteLocationModal(false);
      setLocationToDelete(null);
    }
  };

  const cancelDeleteLocation = () => {
    setShowDeleteLocationModal(false);
    setLocationToDelete(null);
  };

  // Handle Rota button click
  const handleRotaClick = () => {
    // Check if user has Growth plan or above
    if (!['growth', 'pro', 'enterprise'].includes(planInfo?.plan) && !planInfo?.is_vip) {
      toast.error('Rota management is only available for Growth plan and above');
      setShowPaymentRequired(true);
      return;
    }
    
    if (locations.length === 0) {
      toast.error('No locations available');
    } else if (locations.length === 1) {
      // Direct to rota builder
      navigate(`/rota/${locations[0].id}`);
    } else {
      // Show location selection modal
      setShowRotaLocationSelect(true);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/manager/logout?session_id=${sessionId}`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    setIsLoggedIn(false);
    setSessionId('');
    setCurrentLocation(null);
    
    // Clear persistent session data
    localStorage.removeItem('tyme_session');
    localStorage.removeItem('tyme_user');
    
    toast.success('Logged out successfully');
  };

  // Load locations
  const loadLocations = async () => {
    try {
      // Track user activity to prevent false logouts
      window.lastActivityTime = Date.now();
      
      console.log('Loading locations for session:', sessionId);
      const response = await axios.get(`${API}/manager/locations?session_id=${sessionId}`);
      console.log('Locations loaded successfully:', response.data.locations.length, 'locations');
      setLocations(response.data.locations);
      
      // If no current location set, set the first one
      if (!currentLocation && response.data.locations.length > 0) {
        setCurrentLocation(response.data.locations[0]);
      }
    } catch (error) {
      console.error('Locations loading error:', error);
      // Only show error if it's not a recent login timing issue
      const sessionAge = sessionId ? Date.now() - (window.loginTime || 0) : Infinity;
      const recentLogin = sessionAge < 5000;
      
      if (!recentLogin) {
        handleApiError(error, 'loading locations');
      } else {
        console.log('Ignoring locations error due to recent login timing');
      }
    }
  };

  // Fetch plan information
  const fetchPlanInfo = async () => {
    if (!sessionId) return;
    
    setIsPlanLoading(true);
    try {
      const response = await axios.get(`${API}/user/plan-info?session_id=${sessionId}`);
      setPlanInfo(response.data);
    } catch (error) {
      console.error('Error fetching plan info:', error);
      // Set a default plan info to prevent infinite loading screen
      setPlanInfo({
        plan: 'starter',
        limits: { max_employees: 10, max_locations: 1 },
        current_usage: { locations: 0 },
        is_vip: false
      });
    } finally {
      setIsPlanLoading(false);
    }
  };

  // Subscription Management Functions
  const fetchSubscriptionStatus = async () => {
    if (!sessionId) return;
    
    try {
      const response = await axios.get(`${API}/user/subscription-status?session_id=${sessionId}`);
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const response = await axios.post(`${API}/user/subscription/cancel?session_id=${sessionId}`);
      
      if (response.data.success) {
        toast.success('Subscription cancelled successfully. You will be logged out.');
        setShowSubscriptionManagement(false);
        
        // Clear session and redirect to homepage after short delay
        setTimeout(() => {
          localStorage.removeItem('session');
          setIsLoggedIn(false);
          setSessionId(null);
          setPlanInfo(null);
          setCurrentLocation(null);
          toast.info('Please resubscribe to regain access to your dashboard.');
        }, 2000);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('VIP users cannot cancel subscription. Please contact support.');
      } else {
        toast.error(error.response?.data?.detail || 'Error cancelling subscription');
      }
    }
  };

  const handleUpgradeSubscription = async (targetPlan) => {
    if (!targetPlan) return;
    
    const currentPlan = subscriptionStatus?.plan || 'starter';
    const planHierarchy = {"starter": 1, "growth": 2, "pro": 3, "enterprise": 4};
    const isUpgrade = planHierarchy[targetPlan] > planHierarchy[currentPlan];
    const action = isUpgrade ? 'upgrade' : 'downgrade';
    
    try {
      const response = await axios.post(`${API}/user/subscription/change-plan?session_id=${sessionId}`, {
        action: action,
        new_plan: targetPlan
      });
      
      if (response.data.success && response.data.stripe_url) {
        const changeType = response.data.change_type || 'upgrade';
        
        // Show appropriate message based on upgrade/downgrade
        if (changeType === 'upgrade') {
          toast.success(`Redirecting to payment for ${targetPlan.toUpperCase()} plan. Your current subscription will be cancelled.`);
        } else {
          toast.info(`Redirecting to ${targetPlan.toUpperCase()} plan. Change takes effect at end of billing period.`);
        }
        
        // Open Stripe payment page
        window.open(response.data.stripe_url, '_blank');
        setShowSubscriptionManagement(false);
        
        // Set up a poll to check for plan update (after user completes payment)
        const checkPlanUpdate = setInterval(async () => {
          try {
            await recheckPlan();
            // If plan changed, stop polling
            clearInterval(checkPlanUpdate);
          } catch (e) {
            // Keep polling
          }
        }, 5000); // Check every 5 seconds
        
        // Stop polling after 5 minutes
        setTimeout(() => clearInterval(checkPlanUpdate), 300000);
      }
      
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('VIP users cannot change subscription. Please contact support.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || 'Cannot change to this plan');
      } else {
        toast.error(error.response?.data?.detail || 'Error changing subscription');
      }
    }
  };

  // Create new location
  const handleCreateLocation = async (e) => {
    e.preventDefault();
    if (!newLocationName.trim()) return;

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/manager/locations?session_id=${sessionId}`, {
        name: newLocationName.trim()
      });
      
      toast.success('Location created successfully');
      setAddLocationDialog(false);
      setNewLocationName('');
      
      // Reload locations and set the new one as current
      await loadLocations();
      
      // Find the new location and set it as current
      const newLocation = { id: response.data.location_id, name: newLocationName.trim() };
      setCurrentLocation(newLocation);
    } catch (error) {
      const message = error.response?.data?.detail || 'Error creating location';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Switch location
  const switchLocation = (location) => {
    setCurrentLocation(location);
    // Clear current data to force reload for new location
    setLiveStatus(null);
    setEmployees([]);
    setReportInfo(null);
    setAutomatedReports([]);
    setLeaveAnalytics(null);
    
    // Load data for new location based on active tab
    if (activeTab === 'live-status') {
      loadLiveStatus();
    } else if (activeTab === 'employees') {
      loadEmployees();
      loadLeaveAnalytics();
    } else if (activeTab === 'reports') {
      loadReportStatus();
      loadAutomatedReports();
      loadAvailableMonths();
    } else if (activeTab === 'attendance-form') {
      loadCustomization();
    }
  };

  // Load customization settings
  const loadCustomization = async () => {
    if (!currentLocation) return;
    
    try {
      const response = await axios.get(`${API}/manager/locations/${currentLocation.id}/customization?session_id=${sessionId}`);
      setCustomization(response.data);
    } catch (error) {
      handleApiError(error, 'loading customization');
    }
  };

  // Save customization settings
  const saveCustomization = async (updates) => {
    if (!currentLocation) {
      console.warn('saveCustomization: No current location');
      return;
    }
    if (!sessionId) {
      console.warn('saveCustomization: No valid session');
      toast.error('Session expired. Please login again.');
      return;
    }
    
    try {
      setIsLoading(true);
      await axios.put(`${API}/manager/locations/${currentLocation.id}/customization?session_id=${sessionId}`, updates);
      
      // Update local state
      setCustomization(prev => ({ ...prev, ...updates }));
      toast.success('Customization saved successfully');
    } catch (error) {
      handleApiError(error, 'saving customization');
      const message = error.response?.data?.detail || 'Error saving customization';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle logo upload
  const handleLogoUpload = async (file) => {
    if (!currentLocation || !file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/manager/locations/${currentLocation.id}/logo?session_id=${sessionId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Update customization state
      setCustomization(prev => ({
        ...prev,
        logo_url: response.data.logo_url,
        logo_filename: response.data.filename
      }));

      toast.success('Logo uploaded successfully');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error uploading logo';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Remove logo
  const removeLogo = async () => {
    if (!currentLocation) return;
    
    try {
      setIsLoading(true);
      await axios.delete(`${API}/manager/locations/${currentLocation.id}/logo?session_id=${sessionId}`);
      
      // Update local state
      setCustomization(prev => ({ ...prev, logo_url: null, logo_filename: null }));
      toast.success('Logo removed successfully');
    } catch (error) {
      handleApiError(error, 'removing logo');
      toast.error('Error removing logo');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle break setting changes
  const handleBreakSettingChange = (field, value) => {
    setCustomization(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save break settings
  const saveBreakSettings = async () => {
    if (!currentLocation) {
      console.warn('saveBreakSettings: No current location');
      return;
    }
    if (!sessionId) {
      console.warn('saveBreakSettings: No valid session');
      toast.error('Session expired. Please login again.');
      return;
    }
    
    try {
      setIsLoading(true);
      
      const breakSettings = {
        workday_hours: customization.workday_hours || 8.0,
        workday_start_time: customization.workday_start_time || "09:00",
        workday_end_time: customization.workday_end_time || "17:00",
        standard_break_minutes: customization.standard_break_minutes || 30,
        break_deduction_enabled: customization.break_deduction_enabled || false
      };
      
      // Use the correct endpoint - same as saveCustomization
      await axios.put(
        `${API}/manager/locations/${currentLocation.id}/customization?session_id=${sessionId}`,
        breakSettings
      );
      
      // Update local state
      setCustomization(prev => ({
        ...prev,
        ...breakSettings
      }));
      toast.success('Break settings saved successfully');
    } catch (error) {
      handleApiError(error, 'saving break settings');
      const message = error.response?.data?.detail || 'Error saving break settings';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Save a single setting immediately (for toggles)
  const saveSingleSetting = async (key, value) => {
    if (!currentLocation || !sessionId) {
      console.warn('saveSingleSetting: No location or session');
      return false;
    }
    
    try {
      await axios.put(
        `${API}/manager/locations/${currentLocation.id}/customization?session_id=${sessionId}`,
        { [key]: value }
      );
      return true;
    } catch (error) {
      console.error('Error saving setting:', error);
      toast.error('Failed to save setting');
      return false;
    }
  };

  // getFilteredEmployees function is already defined above with enhanced leave analytics filtering

  // Clear search and filters
  const clearSearchAndFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  // Open edit PIN dialog
  const openEditPinDialog = (employee) => {
    setSelectedEmployee(employee);
    setEditPinForm({ pin: '' });
    setEditPinDialog(true);
  };

  // Open edit name dialog (now includes PIN viewing/editing)
  const openEditNameDialog = (employee) => {
    setSelectedEmployee(employee);
    setEditNameForm({ 
      first_name: employee.first_name, 
      last_name: employee.last_name,
      pin: employee.pin || '',  // Include current PIN
      email: employee.email || ''  // Include current email
    });
    setShowPinInEdit(false);  // Reset PIN visibility
    setEditNameDialog(true);
  };

  // Open delete dialog
  const openDeleteDialog = (employee) => {
    setSelectedEmployee(employee);
    setDeleteDialog(true);
  };

  // Open break settings dialog
  const openBreakSettingsDialog = async (employee) => {
    setSelectedEmployee(employee);
    
    // Load existing break override if it exists
    try {
      const response = await axios.get(
        `${API}/manager/employee-breaks/${currentLocation.id}`,
        { params: { session_id: sessionId } }
      );
      
      const existingOverride = response.data.overrides.find(
        override => override.employee_id === employee.id
      );
      
      if (existingOverride) {
        setEmployeeBreakForm({ 
          custom_break_minutes: existingOverride.custom_break_minutes,
          custom_work_hours: existingOverride.custom_work_hours || customization?.workday_hours || 8.0
        });
      } else {
        setEmployeeBreakForm({ 
          custom_break_minutes: customization?.standard_break_minutes || 30,
          custom_work_hours: customization?.workday_hours || 8.0
        });
      }
    } catch (error) {
      // Use default if error loading overrides
      setEmployeeBreakForm({ 
        custom_break_minutes: customization?.standard_break_minutes || 30,
        custom_work_hours: customization?.workday_hours || 8.0
      });
    }
    
    setBreakSettingsDialog(true);
  };

  // Handle break settings update
  const handleUpdateBreakSettings = async () => {
    setIsLoading(true);

    try {
      await axios.put(
        `${API}/manager/employee-breaks/${currentLocation.id}/${selectedEmployee.id}`,
        employeeBreakForm,
        { params: { session_id: sessionId } }
      );
      
      toast.success(`Break settings updated for ${selectedEmployee.first_name}`);
      setBreakSettingsDialog(false);
    } catch (error) {
      handleApiError(error, 'updating break settings');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle PIN update
  const handleUpdatePin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.put(
        `${API}/manager/employees/${selectedEmployee.id}/pin?session_id=${sessionId}`,
        { pin: editPinForm.pin }
      );
      toast.success('Employee PIN updated successfully');
      setEditPinDialog(false);
      loadEmployees();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error updating PIN';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle name and PIN update
  const handleUpdateName = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Prepare update data
      const updateData = {
        first_name: editNameForm.first_name,
        last_name: editNameForm.last_name,
        email: editNameForm.email || null  // Include email
      };
      
      // Update name and email
      await axios.put(
        `${API}/manager/employees/${selectedEmployee.id}?session_id=${sessionId}`,
        updateData
      );
      
      // Update PIN if it was changed and is valid
      if (editNameForm.pin && editNameForm.pin.length === 4 && editNameForm.pin !== selectedEmployee.pin) {
        await axios.put(
          `${API}/manager/employees/${selectedEmployee.id}?session_id=${sessionId}`,
          { pin: editNameForm.pin }
        );
        toast.success('Employee details and PIN updated successfully');
      } else {
        toast.success('Employee details updated successfully');
      }
      
      setEditNameDialog(false);
      loadEmployees();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error updating employee';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle employee deletion
  const handleDeleteEmployee = async () => {
    setIsLoading(true);

    try {
      await axios.delete(`${API}/manager/employees/${selectedEmployee.id}?session_id=${sessionId}`);
      toast.success('Employee deleted successfully');
      setDeleteDialog(false);
      loadEmployees();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error deleting employee';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Staff Hours Overview Functions
  const fetchStaffHours = async (period = staffHoursPeriod, search = staffHoursSearch) => {
    if (!sessionId) return;
    
    setStaffHoursLoading(true);
    try {
      const response = await axios.get(`${API}/manager/staff-hours-unified?session_id=${sessionId}&period=${period}&search=${search}`);
      setStaffHours(response.data.staff_hours || []);
      
      // Generate available months starting from September 2025 (when app was created)
      const months = [];
      const today = new Date();
      const startDate = new Date(2025, 8, 1); // September 2025 (month is 0-indexed)
      
      // Generate months from September 2025 to current month
      let currentDate = new Date(startDate);
      while (currentDate <= today) {
        months.push({
          value: `month_${currentDate.getFullYear()}_${currentDate.getMonth() + 1}`,
          label: currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
        });
        
        // Move to next month
        currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
      }
      
      // Reverse to show most recent months first
      setStaffHoursAvailableMonths(months.reverse());
    } catch (error) {
      handleApiError(error, 'fetching staff hours');
      setStaffHours([]);
      // Silently fail - don't show error toasts for data loading
    } finally {
      setStaffHoursLoading(false);
    }
  };

  const downloadEmployeeCsv = async (employeeId, employeeName) => {
    // Check plan access - individual reports require Growth+
    if (planInfo && planInfo.plan === 'starter' && !planInfo.is_vip) {
      toast.error('Individual employee reports are available on Growth plan and above.');
      return;
    }

    if (!currentLocation?.id) {
      toast.error('Please select a location first');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/manager/staff-hours/${currentLocation.id}/employee/${employeeId}/csv?session_id=${sessionId}&period=${staffHoursPeriod}`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${employeeName.replace(' ', '_')}_hours_${staffHoursPeriod}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${employeeName}'s hours report`);
    } catch (error) {
      handleApiError(error, 'downloading employee CSV');
      if (error.response?.status === 403) {
        let detail = 'This feature requires a Pro plan or above. Please upgrade to access individual employee reports.';
        if (error.response?.data instanceof Blob) {
          try { const text = await error.response.data.text(); const json = JSON.parse(text); detail = json.detail || detail; } catch(e) {}
        }
        toast.error(detail);
      } else if (error.response?.status !== 401) {
        let detail = 'Error downloading employee report';
        if (error.response?.data instanceof Blob) {
          try { const text = await error.response.data.text(); const json = JSON.parse(text); detail = json.detail || detail; } catch(e) {}
        }
        toast.error(detail);
      }
    }
  };

  const downloadAllEmployeesCsv = async () => {
    // Check plan access - bulk reports require Growth+
    if (planInfo && planInfo.plan === 'starter' && !planInfo.is_vip) {
      toast.error('Bulk employee reports are available on Growth plan and above.');
      return;
    }

    if (!currentLocation?.id) {
      toast.error('Please select a location first');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/manager/staff-hours/${currentLocation.id}/all-employees-csv?session_id=${sessionId}&period=${staffHoursPeriod}`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename based on period
      let periodName = '';
      if (staffHoursPeriod === 'this_week') {
        periodName = 'This_Week';
      } else if (staffHoursPeriod.startsWith('month_')) {
        const parts = staffHoursPeriod.split('_');
        const monthName = new Date(parts[1], parts[2] - 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }).replace(' ', '_');
        periodName = monthName;
      }
      
      link.download = `All_Employee_Hours_${periodName}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Downloaded all employees hours report');
    } catch (error) {
      handleApiError(error, 'downloading all employees CSV');
      if (error.response?.status === 403) {
        let detail = 'This feature requires a Pro plan or above. Please upgrade to access bulk employee reports.';
        if (error.response?.data instanceof Blob) {
          try { const text = await error.response.data.text(); const json = JSON.parse(text); detail = json.detail || detail; } catch(e) {}
        }
        toast.error(detail);
      } else if (error.response?.status !== 401) {
        let detail = 'Error downloading all employees report';
        if (error.response?.data instanceof Blob) {
          try { const text = await error.response.data.text(); const json = JSON.parse(text); detail = json.detail || detail; } catch(e) {}
        }
        toast.error(detail);
      }
    }
  };

  // Email automation functions
  const loadEmailAutomation = async () => {
    if (!currentLocation || !planInfo || (!['growth', 'pro', 'enterprise'].includes(planInfo.plan) && !planInfo.is_vip)) return;
    
    setEmailAutomationLoading(true);
    try {
      const response = await axios.get(
        `${API}/user/email-automation/${currentLocation.id}?session_id=${sessionId}`
      );
      setEmailAutomation(response.data);
    } catch (error) {
      console.error('Error loading email automation:', error);
    } finally {
      setEmailAutomationLoading(false);
    }
  };

  const saveEmailAutomation = async () => {
    if (!currentLocation) return;
    
    setEmailAutomationLoading(true);
    try {
      await axios.post(
        `${API}/user/email-automation?session_id=${sessionId}`,
        {
          location_id: currentLocation.id,
          email_address: emailAutomation.email_address,
          delivery_day: emailAutomation.delivery_day
        }
      );
      toast.success('Email automation settings saved successfully');
      setShowEmailAutomationDialog(false);
      await loadEmailAutomation();
    } catch (error) {
      handleApiError(error, 'saving email automation');
      if (error.response?.status === 403) {
        toast.error('Email automation is only available for Pro plan and above');
      }
    } finally {
      setEmailAutomationLoading(false);
    }
  };

  const deleteEmailAutomation = async () => {
    if (!currentLocation) return;
    
    setEmailAutomationLoading(true);
    try {
      await axios.delete(
        `${API}/user/email-automation/${currentLocation.id}?session_id=${sessionId}`
      );
      toast.success('Email automation disabled');
      setShowEmailAutomationDialog(false);
      setEmailAutomation({
        exists: false,
        email_address: '',
        delivery_day: 'monday',
        enabled: true
      });
    } catch (error) {
      handleApiError(error, 'deleting email automation');
    } finally {
      setEmailAutomationLoading(false);
    }
  };

  // Weekly Hours Email functions (Available to All Plans)
  const loadWeeklyHoursEmail = async () => {
    if (!currentLocation || !planInfo) return;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/weekly-hours-email/${currentLocation.id}?session_id=${sessionId}`
      );
      
      if (response.data) {
        setWeeklyHoursEmail(response.data);
      }
    } catch (error) {
      console.error('Error loading weekly hours email:', error);
    }
  };

  const saveWeeklyHoursEmail = async () => {
    if (!currentLocation) return;
    
    // Validate email
    if (!weeklyHoursEmail.email_address || !weeklyHoursEmail.email_address.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setWeeklyHoursEmailLoading(true);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/weekly-hours-email?session_id=${sessionId}`,
        {
          location_id: currentLocation.id,
          email_address: weeklyHoursEmail.email_address,
          delivery_day: weeklyHoursEmail.delivery_day,
          frequency: weeklyHoursEmail.frequency || 'weekly'
        }
      );
      
      if (response.data.success) {
        toast.success('Report email settings saved!');
        loadWeeklyHoursEmail();
      }
    } catch (error) {
      handleApiError(error, 'saving weekly hours email settings');
    } finally {
      setWeeklyHoursEmailLoading(false);
    }
  };

  const deleteWeeklyHoursEmail = async () => {
    if (!currentLocation) return;
    
    setWeeklyHoursEmailLoading(true);
    
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/weekly-hours-email/${currentLocation.id}?session_id=${sessionId}`
      );
      
      if (response.data.success) {
        toast.success('Weekly hours email deleted successfully');
        setWeeklyHoursEmail({
          exists: false,
          email_address: '',
          delivery_day: 'friday',
          frequency: 'weekly',
          enabled: true
        });
        loadWeeklyHoursEmail();
      }
    } catch (error) {
      handleApiError(error, 'deleting weekly hours email');
    } finally {
      setWeeklyHoursEmailLoading(false);
    }
  };

  const handleManualPlanUpdate = async (newPlan) => {
    try {
      const response = await axios.post(
        `${API}/user/manual-plan-update?session_id=${sessionId}`,
        { plan: newPlan }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowSubscriptionManagement(false);
        // Refresh plan info and page
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating plan');
    }
  };

  const handleStaffHoursSearchChange = (value) => {
    setStaffHoursSearch(value);
    // Debounce search to avoid too many API calls
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      fetchStaffHours(staffHoursPeriod, value);
    }, 500);
  };

  const handleStaffHoursPeriodChange = (period) => {
    setStaffHoursPeriod(period);
    fetchStaffHours(period, staffHoursSearch);
  };

  let searchTimeout;

  // Load data when tab changes
  useEffect(() => {
    if (isLoggedIn && sessionId) {
      if (!locations.length) {
        loadLocations();
      }
      
      if (currentLocation) {
        if (activeTab === 'live-status') {
          loadLiveStatus();
        } else if (activeTab === 'employees') {
          loadEmployees();
          loadEmailSettings();
        } else if (activeTab === 'reports') {
          loadReportStatus();
          loadAutomatedReports();
          loadAvailableMonths();
          loadCustomization(); // Load break settings for Daily Break Settings
        } else if (activeTab === 'attendance-form') {
          loadCustomization();
        }
      }
    }
  }, [activeTab, isLoggedIn, sessionId, currentLocation]);

  // Load all settings when currentLocation is set (ensures persistence on return)
  useEffect(() => {
    if (isLoggedIn && sessionId && currentLocation) {
      // Load customization settings (includes break settings)
      loadCustomization();
    }
  }, [currentLocation, isLoggedIn, sessionId]);

  // Load email automation settings for Business plan users
  useEffect(() => {
    if (isLoggedIn && sessionId && (hasRotaBuilder)) {
      loadEmailAutomation();
    }
  }, [isLoggedIn, sessionId, planInfo]);

  // Load weekly hours email settings for all plans
  useEffect(() => {
    if (isLoggedIn && sessionId && planInfo && activeTab === 'reports') {
      loadWeeklyHoursEmail();
    }
  }, [isLoggedIn, sessionId, planInfo, activeTab]);

  // Load plan info, subscription status, and dashboard data when logged in
  useEffect(() => {
    if (isLoggedIn && sessionId) {
      const loadInitialData = async () => {
        try {
          // Load all data in parallel for faster loading
          const promises = [];
          
          if (!planInfo) {
            promises.push(fetchPlanInfo());
          }
          
          if (!subscriptionStatus) {
            promises.push(fetchSubscriptionStatus());
          }
          
          // Load locations for backwards compatibility
          promises.push(
            axios.get(`${API}/manager/locations?session_id=${sessionId}`)
              .then(res => {
                if (res.data.locations?.length > 0) {
                  setLocations(res.data.locations);
                  const firstLocation = res.data.locations[0];
                  if (!currentLocation) {
                    setCurrentLocation(firstLocation);
                  }
                  // Load email settings from the location data
                  if (firstLocation?.email_report_settings) {
                    setEmailSettings(firstLocation.email_report_settings);
                  }
                }
              })
              .catch(() => {})
          );
          
          // Load unified dashboard data
          promises.push(loadLiveStatus());
          
          await Promise.all(promises);
        } catch (error) {
          console.error('Error loading initial data:', error);
        }
      };
      
      loadInitialData();
    }
  }, [isLoggedIn, sessionId]);

  // Load staff hours when location changes or when switching to reports tab
  useEffect(() => {
    if (isLoggedIn && sessionId && activeTab === 'reports') {
      fetchStaffHours();
    }
  }, [activeTab, isLoggedIn, sessionId]);

  // Load leave analytics when period changes
  useEffect(() => {
    if (isLoggedIn && sessionId && activeTab === 'employees') {
      loadLeaveAnalytics();
    }
  }, [leaveAnalyticsPeriod, activeTab, isLoggedIn, sessionId]);

  // Auto-refresh for LIVE updates - refreshes every 10 seconds when on live-status tab
  useEffect(() => {
    if (!autoRefreshEnabled || !isLoggedIn || !sessionId) {
      return;
    }

    let intervalId;

    if (activeTab === 'live-status') {
      // Refresh live status every 10 seconds
      intervalId = setInterval(() => {
        loadLiveStatus();
      }, 10000);
    } else if (activeTab === 'employees') {
      // Refresh employees list every 30 seconds  
      intervalId = setInterval(() => {
        loadEmployees();
      }, 30000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefreshEnabled, activeTab, isLoggedIn, sessionId]);

  // Track page views and engagement
  useEffect(() => {
    if (isLoggedIn && activeTab) {
      analytics.trackPageView(`Dashboard - ${activeTab}`, window.location.href);
      analytics.trackEngagement(`dashboard_${activeTab}`);
    }
  }, [activeTab, isLoggedIn]);

  // Track payment page view
  useEffect(() => {
    if (showPaymentRequired) {
      analytics.trackPageView('Payment Required', window.location.href);
      analytics.trackDemoRequest(); // Track as demo/trial interest
    }
  }, [showPaymentRequired]);

  if (!isLoggedIn) {
    return (
      <AuthScreens
        loginForm={loginForm} setLoginForm={setLoginForm}
        signupForm={signupForm} setSignupForm={setSignupForm}
        verificationForm={verificationForm} setVerificationForm={setVerificationForm}
        forgotPasswordForm={forgotPasswordForm} setForgotPasswordForm={setForgotPasswordForm}
        forgotPasswordStep={forgotPasswordStep} setForgotPasswordStep={setForgotPasswordStep}
        forgotPasswordMessage={forgotPasswordMessage}
        showEmailVerification={showEmailVerification} setShowEmailVerification={setShowEmailVerification}
        showSignup={showSignup} setShowSignup={setShowSignup}
        showPaymentRequired={showPaymentRequired} setShowPaymentRequired={setShowPaymentRequired}
        showForgotPassword={showForgotPassword} setShowForgotPassword={setShowForgotPassword}
        showTermsModal={showTermsModal} setShowTermsModal={setShowTermsModal}
        showPrivacyModal={showPrivacyModal} setShowPrivacyModal={setShowPrivacyModal}
        isLoading={isLoading}
        passwordErrors={passwordErrors} setPasswordErrors={setPasswordErrors}
        nameError={nameError} setNameError={setNameError}
        emailError={emailError} setEmailError={setEmailError}
        validatePassword={validatePassword} validateFullName={validateFullName} validateEmail={validateEmail}
        handleLogin={handleLogin} handleSignup={handleSignup}
        handleVerifyEmail={handleVerifyEmail} handleResendCode={handleResendCode}
        handleForgotPasswordSendCode={handleForgotPasswordSendCode} handleForgotPasswordReset={handleForgotPasswordReset}
        getStripePaymentUrl={getStripePaymentUrl}
      />
    );
  }


  // Show loading screen while plan info is being fetched
  // This prevents showing wrong plan limits or empty data
  // Added timeout to prevent infinite loading
  if (isLoggedIn && isPlanLoading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <TymeLoader />
          <p className="text-gray-300 mt-4">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // MAIN DASHBOARD RENDER - New Sidebar Layout
  // ============================================================================
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-black text-zinc-100">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-6 lg:py-10">
        {/* Header */}
        <header className="pb-6 lg:pb-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <a href="/" className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500/20 to-violet-500/20 ring-1 ring-zinc-800 hover:ring-zinc-700 transition">
                <Clock className="h-5 w-5 text-emerald-400" />
              </a>
              <div>
                <div className="flex items-center gap-4 flex-wrap">
                  <h1 className="text-xl lg:text-2xl font-semibold">Welcome.</h1>
                  {currentLocation && (
                    <button
                      onClick={() => {
                        const attendanceUrl = `${window.location.origin}/attendance/${currentLocation.id}`;
                        navigator.clipboard.writeText(attendanceUrl).then(() => {
                          toast.success('Attendance link copied to clipboard!');
                        }).catch(() => {
                          toast.error('Failed to copy link');
                        });
                      }}
                      className="group flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800/70 transition"
                    >
                      <span className="tracking-wide">Attendance Form</span>
                      <Copy className="h-4 w-4 opacity-70 group-hover:opacity-100" />
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 flex-wrap">
              {/* Subscription badge */}
              {subscriptionStatus && (
                <button
                  onClick={() => setShowSubscriptionManagement(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-500/20 to-emerald-500/20 rounded-xl text-sm text-zinc-200 hover:from-violet-500/30 hover:to-emerald-500/30 transition border border-zinc-800"
                >
                  <CreditCard className="w-4 h-4" />
                  {subscriptionStatus.is_trial 
                    ? `Trial: ${subscriptionStatus.trial_days_remaining || 0} days`
                    : `${subscriptionStatus.plan?.charAt(0).toUpperCase() + subscriptionStatus.plan?.slice(1)} Plan`
                  }
                  {subscriptionStatus.trial_expired && (
                    <AlertTriangle className="w-4 h-4 text-amber-400 ml-1" />
                  )}
                </button>
              )}
              
              <Button 
                onClick={handleLogout}
                className="rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200"
              >
                <LogOut className="mr-2 h-4 w-4" /> Logout
              </Button>
            </div>
          </div>
        </header>

        {/* Main Layout - Sidebar + Content */}
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6 lg:gap-12">
          {/* Sidebar Navigation */}
          <aside className="space-y-2 lg:block hidden">
            <SidebarItem
              icon={<BarChart3 size={16} />}
              label="Live Status"
              active={activeTab === 'live-status'}
              onClick={() => setActiveTab('live-status')}
            />
            <SidebarItem
              icon={<Users size={16} />}
              label="Employees"
              active={activeTab === 'employees'}
              onClick={() => setActiveTab('employees')}
            />
            <SidebarItem
              icon={<Clock size={16} />}
              label="Hours"
              active={activeTab === 'reports'}
              onClick={() => setActiveTab('reports')}
            />
            <SidebarItem
              icon={<Settings size={16} />}
              label="Attendance Rules"
              active={activeTab === 'attendance-rules'}
              onClick={() => setActiveTab('attendance-rules')}
            />
            
            {/* Rota tab - Growth plan and above only */}
            <div className="pt-4 border-t border-zinc-800 mt-4">
              <SidebarItem
                icon={<CalendarDays size={16} />}
                label="Rota Builder"
                active={activeTab === 'rota'}
                onClick={() => {
                  if (hasRotaBuilder) {
                    setActiveTab('rota');
                  } else {
                    toast.error('Rota Builder requires the Growth plan or above');
                    setShowSubscriptionManagement(true);
                  }
                }}
                badge={!hasRotaBuilder ? 'Growth' : null}
              />
            </div>
            
            {/* Logout in sidebar for consistency */}
            <div className="pt-4 mt-4 border-t border-zinc-800">
              <Button 
                onClick={handleLogout}
                className="w-full rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200"
              >
                <LogOut className="mr-2 h-4 w-4" /> Logout
              </Button>
            </div>
          </aside>

          {/* Mobile Navigation */}
          <div className="lg:hidden flex gap-2 overflow-x-auto pb-4">
            <Button
              variant={activeTab === 'live-status' ? 'default' : 'outline'}
              onClick={() => setActiveTab('live-status')}
              className={`rounded-xl whitespace-nowrap ${activeTab === 'live-status' ? 'bg-emerald-500/20 text-emerald-200' : 'bg-zinc-800 text-zinc-300'}`}
            >
              <BarChart3 size={14} className="mr-1" /> Live
            </Button>
            <Button
              variant={activeTab === 'employees' ? 'default' : 'outline'}
              onClick={() => setActiveTab('employees')}
              className={`rounded-xl whitespace-nowrap ${activeTab === 'employees' ? 'bg-emerald-500/20 text-emerald-200' : 'bg-zinc-800 text-zinc-300'}`}
            >
              <Users size={14} className="mr-1" /> Employees
            </Button>
            <Button
              variant={activeTab === 'reports' ? 'default' : 'outline'}
              onClick={() => setActiveTab('reports')}
              className={`rounded-xl whitespace-nowrap ${activeTab === 'reports' ? 'bg-emerald-500/20 text-emerald-200' : 'bg-zinc-800 text-zinc-300'}`}
            >
              <Clock size={14} className="mr-1" /> Hours
            </Button>
            <Button
              variant={activeTab === 'attendance-rules' ? 'default' : 'outline'}
              onClick={() => setActiveTab('attendance-rules')}
              className={`rounded-xl whitespace-nowrap ${activeTab === 'attendance-rules' ? 'bg-emerald-500/20 text-emerald-200' : 'bg-zinc-800 text-zinc-300'}`}
            >
              <Settings size={14} className="mr-1" /> Rules
            </Button>
            <Button
              variant={activeTab === 'rota' ? 'default' : 'outline'}
              onClick={() => {
                if (hasRotaBuilder) {
                  setActiveTab('rota');
                } else {
                  toast.error('Rota Builder requires Growth plan or above');
                  setShowSubscriptionManagement(true);
                }
              }}
              className={`rounded-xl whitespace-nowrap ${activeTab === 'rota' ? 'bg-emerald-500/20 text-emerald-200' : 'bg-zinc-800 text-zinc-300'}`}
            >
              <CalendarDays size={14} className="mr-1" /> Rota
            </Button>
          </div>

          {/* Main Content Area */}
          <main className="space-y-8">

            {/* ==================== LIVE STATUS TAB ==================== */}
            {activeTab === 'live-status' && (
              <LiveStatusTab
                liveStatus={liveStatus}
                planInfo={planInfo}
                hasRotaBuilder={hasRotaBuilder}
                currentLocation={currentLocation}
                sessionId={sessionId}
                closeEmployeeSession={closeEmployeeSession}
                setShowSubscriptionManagement={setShowSubscriptionManagement}
              />
            )}

            {/* ==================== EMPLOYEES TAB ==================== */}
            {activeTab === 'employees' && (
              <EmployeesTab
                employees={employees}
                searchTerm={searchTerm} setSearchTerm={setSearchTerm}
                newEmployeeForm={newEmployeeForm} setNewEmployeeForm={setNewEmployeeForm}
                handleCreateEmployee={handleCreateEmployee}
                isLoading={isLoading}
                getFilteredEmployees={getFilteredEmployees}
                setSelectedEmployee={setSelectedEmployee}
                setEmployeeStatusUpdate={setEmployeeStatusUpdate}
                setShowStatusModal={setShowStatusModal}
                setEditPinForm={setEditPinForm}
                setEditPinDialog={setEditPinDialog}
                setEditNameForm={setEditNameForm}
                setEditNameDialog={setEditNameDialog}
                setEmployeeToEdit={setEmployeeToEdit}
                setShowScheduleEditor={setShowScheduleEditor}
                setEmployeeBreakForm={setEmployeeBreakForm}
                setBreakSettingsDialog={setBreakSettingsDialog}
                setDeleteDialog={setDeleteDialog}
                setShowAllScheduleEditor={setShowAllScheduleEditor}
                customization={customization}
              />
            )} 

            {/* ==================== HOURS TAB ==================== */}
            {activeTab === 'reports' && (
              <HoursTab
                customization={customization} setCustomization={setCustomization} saveSingleSetting={saveSingleSetting}
                staffHours={staffHours} staffHoursLoading={staffHoursLoading} staffHoursPeriod={staffHoursPeriod} setStaffHoursPeriod={handleStaffHoursPeriodChange}
                weeklyHoursEmail={weeklyHoursEmail} setWeeklyHoursEmail={setWeeklyHoursEmail} weeklyHoursEmailLoading={weeklyHoursEmailLoading} saveWeeklyHoursEmail={saveWeeklyHoursEmail}
                downloadDaysReport={downloadDaysReport} downloadThisMonthReport={downloadThisMonthReport} downloadMonthReport={downloadMonthReport}
                availableMonths={availableMonths} selectedMonth={selectedMonth} setSelectedMonth={setSelectedMonth}
                isLoading={isLoading}
              />
            )}

            {/* ==================== ATTENDANCE RULES TAB ==================== */}
            {activeTab === 'attendance-rules' && (
              <div className="space-y-10">
                {currentLocation ? (
                  <>
                    {/* Page Header */}
                    <div>
                      <h2 className="text-2xl font-semibold text-white">Attendance Rules</h2>
                      <p className="mt-2 text-sm text-zinc-400 max-w-xl">
                        Define how employees can check in, check out, and how the system handles rule violations.
                      </p>
                    </div>

                    {/* Time Rules Settings Component */}
                    <TimeRulesSettings 
                      locationId={currentLocation.id}
                      sessionId={sessionId}
                    />
                  </>
                ) : (
                  <DashboardPanel className="text-center py-12">
                    <p className="text-zinc-400">Loading location data...</p>
                  </DashboardPanel>
                )}
              </div>
            )}

            {activeTab === 'rota' && (
              <div className="space-y-6">
                {(hasRotaBuilder) && currentLocation ? (
                  <RotaBuilder
                    locationId={currentLocation.id}
                    sessionId={sessionId}
                    employees={employees}
                  />
                ) : (
                  <DashboardPanel className="text-center py-12">
                    <div className="w-16 h-16 bg-gradient-to-r from-violet-500/20 to-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CalendarDays className="w-8 h-8 text-violet-300" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">Rota Builder</h2>
                    <p className="text-zinc-400 mb-6 max-w-md mx-auto">
                      Build weekly schedules, assign shifts, and track wage costs. Available on Growth plan and above.
                    </p>
                    <Button 
                      onClick={() => setShowSubscriptionManagement(true)}
                      className="bg-gradient-to-r from-violet-500/20 to-emerald-500/20 text-white hover:from-violet-500/30 hover:to-emerald-500/30 rounded-xl"
                    >
                      Upgrade to Growth Plan
                    </Button>
                  </DashboardPanel>
                )}
              </div>
            )}

          </main>
        </div>
      </div>

      {/* ==================== ALL MODALS & DIALOGS ==================== */}
      <DashboardModals
        editPinDialog={editPinDialog} setEditPinDialog={setEditPinDialog}
        editPinForm={editPinForm} setEditPinForm={setEditPinForm}
        showPinInEdit={showPinInEdit} setShowPinInEdit={setShowPinInEdit}
        handleUpdatePin={handleUpdatePin}
        editNameDialog={editNameDialog} setEditNameDialog={setEditNameDialog}
        editNameForm={editNameForm} setEditNameForm={setEditNameForm}
        handleUpdateName={handleUpdateName}
        deleteDialog={deleteDialog} setDeleteDialog={setDeleteDialog}
        handleDeleteEmployee={handleDeleteEmployee}
        breakSettingsDialog={breakSettingsDialog} setBreakSettingsDialog={setBreakSettingsDialog}
        employeeBreakForm={employeeBreakForm} setEmployeeBreakForm={setEmployeeBreakForm}
        handleUpdateBreakSettings={handleUpdateBreakSettings}
        showStatusModal={showStatusModal} setShowStatusModal={setShowStatusModal}
        employeeStatusUpdate={employeeStatusUpdate} setEmployeeStatusUpdate={setEmployeeStatusUpdate}
        updateEmployeeStatus={updateEmployeeStatus}
        showScheduleEditor={showScheduleEditor} setShowScheduleEditor={setShowScheduleEditor}
        employeeToEdit={employeeToEdit}
        showAllScheduleEditor={showAllScheduleEditor} setShowAllScheduleEditor={setShowAllScheduleEditor}
        currentLocation={currentLocation}
        sessionId={sessionId}
        loadEmployees={loadEmployees}
        showSubscriptionManagement={showSubscriptionManagement} setShowSubscriptionManagement={setShowSubscriptionManagement}
        subscriptionStatus={subscriptionStatus}
        upgradeTarget={upgradeTarget} setUpgradeTarget={setUpgradeTarget}
        handleUpgradeSubscription={handleUpgradeSubscription}
        selectedEmployee={selectedEmployee}
      />
    </div>
  );
};
function App() {
  return (
    <BrowserRouter>
      <div className="App">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/time-tracking-software" element={<TimeTrackingSoftware />} />
          <Route path="/small-business-time-clock" element={<SmallBusinessTimeClock />} />
          <Route path="/retail-attendance-software" element={<RetailAttendanceSoftware />} />
          <Route path="/kiosk" element={<KioskPage />} />
          <Route path="/attendance/:locationId" element={<AttendancePage />} />
          <Route path="/rota/:locationId" element={<RotaBuilder />} />
          <Route path="/manager-dashboard" element={<Dashboard />} />
          <Route path="/super-admin" element={<SuperAdminDashboard />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster 
          position="top-center"
          theme="dark"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#18181b',
              color: '#e4e4e7',
              border: '1px solid #3f3f46',
              borderRadius: '12px',
              fontSize: '14px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            }
          }}
        />
      </div>
    </BrowserRouter>
  );
}

export default App;